import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Server, Cloud, Wrench, Scaling, Network, Cpu, Gauge, Shield, GitBranch,
  Layers, HardDrive, Activity, Globe, RefreshCw, Lock, Zap, Bell, Workflow,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "vps-hosting": {
    eyebrow: "Hosting · Virtual Servers",
    title: "VPS Hosting",
    sub: "Dedicated CPU, RAM and NVMe with full root access.",
    metaDesc: "VPS hosting with dedicated vCPU, NVMe storage, full root access and snapshots — provisioned in 60 seconds.",
    intro:
      "A Virtual Private Server gives you guaranteed CPU, RAM and storage on a virtualized slice of a dedicated machine — at a fraction of the cost. Full root access, choice of OS, snapshots and the freedom to run any stack.",
    features: [
      { icon: Cpu, title: "Dedicated vCPU", description: "Guaranteed cores — never shared, never throttled." },
      { icon: HardDrive, title: "NVMe storage", description: "Sub-millisecond IO for databases and busy apps." },
      { icon: Lock, title: "Full root access", description: "Install anything, configure anything." },
      { icon: GitBranch, title: "Snapshots", description: "Point-in-time snapshots and one-click rollback." },
      { icon: Shield, title: "DDoS protection", description: "Always-on volumetric DDoS mitigation included." },
      { icon: Zap, title: "60-second provisioning", description: "Server ready and SSH'able in under a minute." },
    ],
    process: [
      { step: "01", title: "Spec", description: "Pick CPU, RAM, storage and region." },
      { step: "02", title: "Choose OS", description: "Ubuntu, Debian, CentOS, AlmaLinux, Windows." },
      { step: "03", title: "Deploy", description: "Push your app or use our stack scripts." },
      { step: "04", title: "Operate", description: "Snapshots, backups, monitoring." },
    ],
    deliverables: ["Provisioned VPS", "SSH key access", "Snapshot policy", "DDoS protection", "Monitoring dashboard"],
    techStack: ["KVM", "NVMe SSD", "Ubuntu / Debian / Alma", "Cloudflare DDoS"],
    pricing: [
      { name: "Starter", price: "$10", period: "month", features: ["2 vCPU / 4 GB RAM", "80 GB NVMe", "4 TB transfer"] },
      { name: "Standard", price: "$25", period: "month", highlighted: true, features: ["4 vCPU / 8 GB RAM", "160 GB NVMe", "Snapshots + backups"] },
      { name: "Power", price: "$80", period: "month", features: ["8 vCPU / 32 GB RAM", "400 GB NVMe", "Unmetered transfer"] },
    ],
    faqs: [
      { q: "Self-managed?", a: "Yes — you have full root. Add Managed VPS for OS patching, security and support." },
      { q: "Can I resize later?", a: "Yes — vertical resize (CPU/RAM/disk) with a single reboot." },
    ],
  },
  "cloud-vps": {
    eyebrow: "Hosting · Virtual Servers",
    title: "Cloud VPS",
    sub: "Hourly-billed cloud instances with API and snapshots.",
    metaDesc: "Cloud VPS with hourly billing, API provisioning, snapshots, floating IPs and private networking.",
    intro:
      "Cloud VPS combines the predictability of a VPS with the elasticity of public cloud. Spin up instances via API, pay by the hour, attach floating IPs and private networks, and tear down anytime.",
    features: [
      { icon: Cloud, title: "Hourly billing", description: "Pay only for the minutes you run." },
      { icon: Workflow, title: "API + Terraform", description: "Provision and destroy via API, CLI or IaC." },
      { icon: Globe, title: "Floating IPs", description: "Re-map IPs across servers for zero-downtime failover." },
      { icon: Layers, title: "Private networks", description: "Free VPCs for secure backend traffic." },
      { icon: GitBranch, title: "Snapshots", description: "Snapshot and clone any instance instantly." },
      { icon: Shield, title: "DDoS protection", description: "Included on every public IP." },
    ],
    process: [
      { step: "01", title: "Connect", description: "API key or Terraform provider setup." },
      { step: "02", title: "Provision", description: "Spin up instances in any region." },
      { step: "03", title: "Network", description: "VPCs, floating IPs and firewalls." },
      { step: "04", title: "Operate", description: "Snapshots, alerts and autoscaling." },
    ],
    deliverables: ["API access", "Terraform provider", "Snapshot policy", "VPC setup", "Monitoring dashboards"],
    techStack: ["KVM", "REST API", "Terraform", "Ansible", "Cloudflare DDoS"],
    pricing: [
      { name: "Hourly", price: "$0.015", period: "hour", features: ["2 vCPU / 4 GB", "80 GB NVMe", "API + snapshots"] },
      { name: "Reserved", price: "$22", period: "month", highlighted: true, features: ["4 vCPU / 8 GB", "160 GB NVMe", "20% saving vs hourly"] },
      { name: "Compute", price: "$90", period: "month", features: ["16 vCPU / 32 GB", "300 GB NVMe", "10 Gbps network"] },
    ],
    faqs: [
      { q: "Can I use Terraform?", a: "Yes — official provider with full resource coverage." },
      { q: "Hourly vs monthly?", a: "Hourly caps at the monthly price, so you never overpay." },
    ],
  },
  "managed-vps": {
    eyebrow: "Hosting · Virtual Servers",
    title: "Managed VPS",
    sub: "We handle the OS, security and stack — you ship the app.",
    metaDesc: "Managed VPS hosting: OS patching, security hardening, stack setup, monitoring and 24/7 support included.",
    intro:
      "All the power of a VPS without the sysadmin overhead. We patch the OS, harden security, install your stack (LEMP, Node, Docker), monitor uptime and respond to incidents 24/7.",
    features: [
      { icon: Wrench, title: "OS patching", description: "Weekly kernel and package updates with rollback." },
      { icon: Shield, title: "Security hardening", description: "Fail2ban, firewall, SSH lockdown, audit logs." },
      { icon: Workflow, title: "Stack setup", description: "LEMP, MEAN, Docker, Kubernetes — installed and tuned." },
      { icon: Activity, title: "24/7 monitoring", description: "Uptime, CPU, RAM, disk and app health checks." },
      { icon: HardDrive, title: "Managed backups", description: "Daily backups with off-site retention." },
      { icon: Bell, title: "Incident response", description: "Engineer paged on alert, RCA within 24 hours." },
    ],
    process: [
      { step: "01", title: "Onboard", description: "Spec, stack and access handover." },
      { step: "02", title: "Harden", description: "Firewall, SSH, fail2ban, audit." },
      { step: "03", title: "Deploy", description: "Stack install, app deploy, smoke test." },
      { step: "04", title: "Operate", description: "Patching, monitoring, incident response." },
    ],
    deliverables: ["Hardened VPS", "Stack installed", "Monitoring + alerts", "Backup policy", "24/7 support"],
    techStack: ["Ubuntu LTS", "Nginx", "PHP/Node", "Docker", "Prometheus + Grafana", "PagerDuty"],
    pricing: [
      { name: "Managed Lite", price: "$60", period: "month", features: ["2 vCPU / 4 GB", "OS patching", "Business-hours support"] },
      { name: "Managed Pro", price: "$150", period: "month", highlighted: true, features: ["4 vCPU / 16 GB", "Stack management", "24/7 support"] },
      { name: "Managed Plus", price: "$350", period: "month", features: ["8 vCPU / 32 GB", "App-level support", "Dedicated DevOps"] },
    ],
    faqs: [
      { q: "Do you support custom stacks?", a: "Yes — anything that runs on Linux, including Kubernetes and bespoke apps." },
      { q: "What's the SLA?", a: "99.99% uptime with credits, and a 15-minute response on critical alerts." },
    ],
  },
  "cloud-hosting": {
    eyebrow: "Hosting · Cloud",
    title: "Cloud Hosting",
    sub: "Multi-region, multi-AZ infrastructure for high availability.",
    metaDesc: "Cloud hosting on multi-region, multi-AZ infrastructure with auto-scaling, load balancing and managed databases.",
    intro:
      "When you need true high availability across regions and availability zones, our cloud platform delivers. Stateless app servers behind load balancers, managed databases with read replicas, object storage and a global CDN.",
    features: [
      { icon: Globe, title: "Multi-region", description: "Deploy across 12+ regions worldwide." },
      { icon: Layers, title: "Multi-AZ", description: "Failover across availability zones per region." },
      { icon: Scaling, title: "Auto-scaling", description: "Scale app servers based on CPU, RPS or queue depth." },
      { icon: HardDrive, title: "Managed DBs", description: "Postgres, MySQL, Redis with backups and replicas." },
      { icon: Network, title: "Global CDN", description: "200+ edge nodes cache assets close to users." },
      { icon: Shield, title: "WAF + DDoS", description: "Web Application Firewall and L3/4/7 DDoS." },
    ],
    process: [
      { step: "01", title: "Architect", description: "Topology, regions, data model." },
      { step: "02", title: "Provision", description: "IaC with Terraform, observability built-in." },
      { step: "03", title: "Migrate", description: "Phased migration with read-replicas." },
      { step: "04", title: "Operate", description: "DR drills, capacity reviews, cost optimization." },
    ],
    deliverables: ["Multi-region topology", "Managed DBs", "CDN + WAF", "IaC repository", "DR playbook"],
    techStack: ["Kubernetes", "Terraform", "Postgres", "Redis", "Cloudflare", "Prometheus"],
    pricing: [
      { name: "Startup", price: "From $300", period: "month", features: ["1 region", "Managed DB", "CDN + WAF"] },
      { name: "Growth", price: "From $1.2k", period: "month", highlighted: true, features: ["Multi-AZ", "Auto-scaling", "Read replicas"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-region", "Dedicated DevOps", "99.99% SLA"] },
    ],
    faqs: [
      { q: "Multi-region active-active?", a: "Yes — for stateless tiers and read-heavy DB workloads. Active-active writes available with conflict resolution." },
      { q: "Vendor lock-in?", a: "We deploy with portable IaC (Terraform + Kubernetes) so you can move providers." },
    ],
  },
  "auto-scaling": {
    eyebrow: "Hosting · Cloud",
    title: "Auto-scaling",
    sub: "Scale up for spikes, scale down to save — automatically.",
    metaDesc: "Auto-scaling for cloud workloads based on CPU, RPS, queue depth or custom metrics — with cost controls.",
    intro:
      "Stop paying for idle capacity and stop crashing under load. Auto-scaling adds and removes instances based on real demand signals — CPU, request rate, queue depth or custom Prometheus metrics — with budget caps and cooldowns to keep costs sane.",
    features: [
      { icon: Scaling, title: "Multiple triggers", description: "CPU, RAM, RPS, queue depth, custom metrics." },
      { icon: Gauge, title: "Predictive scaling", description: "ML-based forecasting for known traffic patterns." },
      { icon: Bell, title: "Cooldowns + caps", description: "Prevent flapping and runaway costs." },
      { icon: Workflow, title: "Scale-to-zero", description: "Batch and dev workloads sleep when idle." },
      { icon: Activity, title: "Health-aware", description: "Unhealthy instances replaced automatically." },
      { icon: Shield, title: "Budget guards", description: "Hard max-instance caps and spend alerts." },
    ],
    process: [
      { step: "01", title: "Profile", description: "Baseline load, peaks and budgets." },
      { step: "02", title: "Configure", description: "Triggers, cooldowns, min/max instances." },
      { step: "03", title: "Test", description: "Synthetic load + chaos drills." },
      { step: "04", title: "Optimize", description: "Tune thresholds based on real traffic." },
    ],
    deliverables: ["Scaling policies", "Predictive forecasts", "Budget caps", "Health checks", "Dashboards"],
    techStack: ["Kubernetes HPA/VPA", "KEDA", "Prometheus", "Karpenter"],
    pricing: [
      { name: "Reactive", price: "Included", features: ["CPU/RAM triggers", "Cooldowns", "Budget caps"] },
      { name: "Advanced", price: "$200", period: "month", highlighted: true, features: ["Custom metrics", "Scale-to-zero", "Multi-tier policies"] },
      { name: "Predictive", price: "$600", period: "month", features: ["ML forecasting", "Capacity planning", "Quarterly review"] },
    ],
    faqs: [
      { q: "Will it scale my database?", a: "Read replicas yes; primary writes use vertical scaling with planned windows." },
      { q: "What about cold starts?", a: "Pre-warmed pools keep latency low; scale-to-zero is opt-in per service." },
    ],
  },
  "load-balancing": {
    eyebrow: "Hosting · Cloud",
    title: "Load Balancing",
    sub: "Global and regional load balancers with health checks and SSL.",
    metaDesc: "Load balancing for cloud apps: L4/L7, global anycast, SSL offload, sticky sessions and health checks.",
    intro:
      "Distribute traffic across instances, AZs and regions for high availability and performance. Layer 4 (TCP) for raw speed, Layer 7 (HTTP/S) for advanced routing, plus global anycast load balancing for sub-50ms response worldwide.",
    features: [
      { icon: Network, title: "L4 + L7", description: "TCP/UDP and HTTP/S with path and host routing." },
      { icon: Globe, title: "Global anycast", description: "Single IP, routed to the nearest region." },
      { icon: Lock, title: "SSL offload", description: "Free auto-managed certs, HTTP/2 + HTTP/3." },
      { icon: Activity, title: "Health checks", description: "Active + passive checks with auto-failover." },
      { icon: RefreshCw, title: "Sticky sessions", description: "Cookie or IP-based session affinity." },
      { icon: Shield, title: "DDoS + WAF", description: "Built-in DDoS scrubbing and WAF rules." },
    ],
    process: [
      { step: "01", title: "Design", description: "Topology, listeners, target groups." },
      { step: "02", title: "Provision", description: "LB, certs, DNS." },
      { step: "03", title: "Cutover", description: "Phased traffic shift with rollback." },
      { step: "04", title: "Monitor", description: "Latency, errors, saturation." },
    ],
    deliverables: ["Load balancer", "SSL certs", "Health checks", "Routing rules", "Observability"],
    techStack: ["HAProxy", "NGINX", "Envoy", "Cloudflare", "Anycast"],
    pricing: [
      { name: "Regional", price: "$15", period: "month", features: ["L4 + L7", "Free SSL", "Health checks"] },
      { name: "Multi-AZ", price: "$45", period: "month", highlighted: true, features: ["Cross-AZ failover", "Sticky sessions", "WAF starter"] },
      { name: "Global", price: "$200", period: "month", features: ["Anycast", "Multi-region", "Advanced WAF"] },
    ],
    faqs: [
      { q: "Can I do blue/green deploys?", a: "Yes — weighted routing and canary rules are first-class." },
      { q: "WebSocket support?", a: "Yes, on L7 with long-lived connections and sticky sessions." },
    ],
  },
};

export const Route = createFileRoute("/hosting/vps-cloud/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/hosting/vps-cloud/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — VPS & Cloud | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — VPS & Cloud | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: VpsCloudChild,
});

function VpsCloudChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
